import { getNetwork } from '../../utils/getNetwork';
import { API_ROOT } from '../../config';

/* =====================================================================
 * schoolsApi.js — API модуля «Школы».
 *
 * Работает напрямую с бэкендом. Адаптер (pick/def/tr) разруливает контракт,
 * как в остальных АРМ (вложенные {ru,kk}, camel/snake, обёртки).
 *
 * Базовый путь — под scadmin (как health): API_ROOT + '/api' + '/schools/...'.
 * Без ?? и ?. — react-app-rewired их не транспилирует.
 * ===================================================================== */

const API_SCHOOLS_ROOT = API_ROOT + '/api'; // https://sc.smartkulsary.kz/api/scadmin/api
const META_ENDPOINT = '/schools/meta';
const DASHBOARD_ENDPOINT = '/schools/dashboard';
const RECORDS_ENDPOINT = '/schools/records'; // POST — создание

export class ApiError extends Error {
  constructor(status, body) {
    super('api_error');
    this.status = status;
    this.body = body;
  }
}

/* --------------------------- helpers адаптера --------------------------- */

const toCamel = (s) => s.replace(/_([a-z])/g, (_, c) => c.toUpperCase());

const pick = (o, key) => {
  if (o === null || o === undefined) return undefined;
  return o[key] !== undefined ? o[key] : o[toCamel(key)];
};

const def = (v, d) => (v === null || v === undefined ? d : v);

// nested {ru,kk} → строка; одноязычная строка → как есть; иначе плоские *_ru/*_kk.
const tr = (o, base, lang) => {
  if (o === null || o === undefined) return undefined;
  const nested = pick(o, base);
  if (nested !== null && typeof nested === 'object') return nested[lang];
  if (typeof nested === 'string') return nested;
  return pick(o, `${base}_${lang}`);
};

const bool = (v) => v === true || v === 'true' || v === 1 || v === '1';
const numOrNull = (v) => (v === null || v === undefined || v === '' ? null : Number(v));

function mapRow(r) {
  return {
    id: pick(r, 'id'),
    name: def(pick(r, 'school_name'), pick(r, 'name')),
    address: def(pick(r, 'address'), pick(r, 'addr')),
    capacity: numOrNull(def(pick(r, 'design_capacity'), pick(r, 'capacity'))),
    shift: numOrNull(pick(r, 'shift')),
    lang_kz: bool(def(pick(r, 'language_kz'), pick(r, 'lang_kz'))),
    lang_ru: bool(def(pick(r, 'language_ru'), pick(r, 'lang_ru'))),
    students: numOrNull(def(pick(r, 'number_of_students'), pick(r, 'students'))),
    teachers: numOrNull(def(pick(r, 'number_of_teachers'), pick(r, 'teachers'))),
    deficit: numOrNull(pick(r, 'deficit')),
    surplus: numOrNull(pick(r, 'surplus')),
  };
}

function mapShift(s) {
  return {
    value: numOrNull(pick(s, 'key')),
    name_ru: pick(s, 'label_ru'),
    name_kk: pick(s, 'label_kk'),
  };
}
function mapLanguage(l) {
  return { code: pick(l, 'key'), name_ru: pick(l, 'label_ru'), name_kk: pick(l, 'label_kk') };
}

/* ------------------------------ Справочник ------------------------------ */
export async function fetchSchoolsMeta() {
  const res = await getNetwork().get(API_SCHOOLS_ROOT + META_ENDPOINT);
  const data = res.data || {};
  const shifts = pick(data, 'shifts') || [];
  const languages = pick(data, 'languages') || [];
  const statuses = pick(data, 'statuses') || [];

  return {
    shifts: shifts.map(mapShift),
    languages: languages.map(mapLanguage),
    statuses: statuses.map(mapLanguage),
  };
}

/* ------------------------------- Перечень ------------------------------- */
export async function fetchSchoolsRows(filters) {
  const f = filters || {};
  const params = {};
  if (f.shift !== null && f.shift !== undefined && f.shift !== '') params.shift = f.shift;
  if (f.lang !== null && f.lang !== undefined && f.lang !== '') params.lang = f.lang;
  if (f.status !== null && f.status !== undefined && f.status !== '') params.status = f.status;

  const res = await getNetwork().get(API_SCHOOLS_ROOT + DASHBOARD_ENDPOINT, { params });
  const data = res.data || {};
  const list = pick(data, 'items') || [];
  return { rows: list.map(mapRow) };
}

/* ------------------------------- Создание ------------------------------- */
// Тело POST: deficit/surplus НЕ отправляем (их считает сервер).
function toBackendPayload(p) {
  return {
    school_name: p.name,
    address: p.address,
    shift: Number(p.shift),
    language_kz: !!p.lang_kz,
    language_ru: !!p.lang_ru,
    design_capacity: Number(p.capacity),
    number_of_students: Number(p.students),
    number_of_teachers: Number(p.teachers),
  };
}

export async function createSchool(payload) {
  const body = toBackendPayload(payload);
  try {
    const res = await getNetwork().post(API_SCHOOLS_ROOT + RECORDS_ENDPOINT, body);
    if (res && (res.status === 201 || res.status === 200)) return mapRow(res.data || {});
    if (res) throw new ApiError(res.status, res.data);
    throw new ApiError(0, null);
  } catch (e) {
    if (e instanceof ApiError) throw e;
    throw new ApiError((e.response && e.response.status) || 0, e.response && e.response.data);
  }
}
